// Where the Draft Certificate is offered, and what of its answer is exported.

import { ChatMessage } from "../../../interfaces/chatSession";

/**
 * Whether this conversation was started by the Quick Analysis action.
 *
 * The Draft Certificate is a step in that workflow - quick analysis, fix what
 * fails, certify - and nowhere else, so this is what decides whether it is
 * offered at all. Read from the messages rather than from any live state
 * because a restored conversation has to answer it the same way.
 */
export function hasTakenQuickAnalysisRoute(messages: ChatMessage[]): boolean {
  return messages.some((m) => m.role === "user" && m.action === "quick_analysis");
}

/**
 * Whether the message at `index` is the answer to a Draft Certificate request.
 *
 * A turn is not always one assistant message. When the model pauses on a
 * playbook-selection card, the user's pick starts a continuation that adds a
 * second assistant message with no user message between the two, so the answer
 * is the LAST assistant message of the run that follows the request - and a
 * run still paused on an unanswered card has not answered yet.
 */
export function isDraftCertificateAnswer(messages: ChatMessage[], index: number): boolean {
  const message = messages[index];
  if (message?.role !== "assistant") return false;
  if (messages[index + 1]?.role === "assistant") return false;
  if (awaitsPlaybookSelection(message)) return false;

  let requestIndex = index - 1;
  while (requestIndex >= 0 && messages[requestIndex].role === "assistant") requestIndex--;
  const request = messages[requestIndex];
  return request?.role === "user" && request.action === "draft_certificate";
}

/** A turn paused on a playbook-selection card the user has not answered. */
function awaitsPlaybookSelection(message: ChatMessage): boolean {
  return (
    message.inlineToolEvents?.some(
      (event) => event.type === "playbookSelection" && !event.data.playbookSelection?.selected
    ) ?? false
  );
}

/**
 * The opening of the certificate proper, as its own prompt mandates it: a
 * `Tier 3 Status:` line followed by a `Final Result:` line. Requiring the pair
 * is what keeps a mention of the words inside a sentence from counting. Not
 * anchored to a line start on purpose: text streamed before and after a tool
 * call is joined with no newline, so a stray sentence can run straight into
 * the header ("...in this conversation.Tier 3 Status: Confirmed").
 */
const CERTIFICATE_HEADER = /(?:\*\*)?Tier 3 Status:[^\n]*\n\s*(?:\*\*)?Final Result:/;

/** A fenced code block boundary, as markdown reads it. */
const FENCE_LINE = /^[ \t]{0,3}(?:```|~~~)/;

/**
 * The part of a certificate answer that belongs in the exported document.
 *
 * Two things are cut. The prompt requires the answer to open with its header,
 * but the model sometimes puts a sentence of its own reasoning before it; that
 * sentence is left in the chat, where it is honest about what happened, and
 * cut from the file, which is the deliverable. And the prompt shows the header
 * inside a code fence, which the model tends to reproduce; the fence goes too,
 * so the header is a paragraph rather than a block of code - and so that the
 * closing fence, left on its own, cannot swallow the rest of the answer into
 * one code block.
 *
 * Where no header can be found the answer is exported whole: an export that
 * silently drops content would be worse than one carrying a stray line.
 */
export function certificateBody(markdown: string): string {
  const match = CERTIFICATE_HEADER.exec(markdown);
  if (!match) return markdown;

  const before = markdown.slice(0, match.index);
  const fromHeader = markdown.slice(match.index);
  const insideFence = before.split("\n").filter((line) => FENCE_LINE.test(line)).length % 2 === 1;
  if (!insideFence) return fromHeader;

  // Drop the closing fence; the opening one went with everything before the header.
  const lines = fromHeader.split("\n");
  const closingFence = lines.findIndex((line) => FENCE_LINE.test(line));
  const body =
    closingFence === -1
      ? lines
      : [...lines.slice(0, closingFence), ...lines.slice(closingFence + 1)];
  return body.join("\n");
}

/** Characters no file system accepts in a name. */
const UNSAFE_FILENAME_CHARS = /[\\/:*?"<>|]/g;

/**
 * Name for the exported file, after the document it certifies.
 *
 * `documentName` is what Word reports, extension included, or its fallback
 * when the document has no URL yet.
 */
export function certificateFilename(documentName: string): string {
  const base = documentName
    .replace(/\.[^.]+$/, "")
    .replace(UNSAFE_FILENAME_CHARS, "")
    .trim();
  return `Tier 3 Draft Certificate - ${base || "Document"}.docx`;
}
